import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useDisclosure,
  HStack,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';
import { tenantHeaders } from '../tenant';

const API_BASE = '/api/v1';

function Configurations() {
  const toast = useToast();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configForm, setConfigForm] = useState({ key: '', value: '' });
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [configToDelete, setConfigToDelete] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
    ...tenantHeaders(),
  });

  const fetchConfigs = () => {
    fetch(`${API_BASE}/configurations/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setConfigs(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const openAddConfig = () => {
    setEditingConfig(null);
    setConfigForm({ key: '', value: '' });
    onModalOpen();
  };

  const openEditConfig = (config) => {
    setEditingConfig(config);
    setConfigForm({
      key: config.key,
      value: config.value,
    });
    onModalOpen();
  };

  const handleSaveConfig = async () => {
    const method = editingConfig ? 'PUT' : 'POST';
    const url = editingConfig ? `${API_BASE}/configurations/${editingConfig.id}/` : `${API_BASE}/configurations/`;
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        // No tenant_id: the service files it under the instance chosen in the sidebar
        body: JSON.stringify(configForm),
      });
      const data = await res.json();
      if (res.ok) {
        fetchConfigs();
        toast({ title: editingConfig ? 'Configuration updated' : 'Configuration created', status: 'success', duration: 3000 });
        onModalClose();
      } else {
        toast({ title: data.error || 'Error saving configuration', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving configuration', status: 'error', duration: 3000 });
    }
  };

  const confirmDelete = (config) => {
    setConfigToDelete(config);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!configToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/configurations/${configToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchConfigs();
        toast({ title: 'Configuration deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Error deleting configuration', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting configuration', status: 'error', duration: 3000 });
    }
    onDeleteClose();
    setConfigToDelete(null);
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="orange.500" /></Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Configurations</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="orange" onClick={openAddConfig}>
          Add Configuration
        </Button>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Key</Th>
              <Th>Value</Th>
              <Th>Updated</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {configs.map(config => (
              <Tr key={config.id}>
                <Td fontWeight="medium">{config.key}</Td>
                <Td>
                  <Text fontSize="sm" noOfLines={1} maxW="300px">
                    {config.key.toLowerCase().includes('key') || config.key.toLowerCase().includes('secret') 
                      ? '••••••••' 
                      : config.value}
                  </Text>
                </Td>
                <Td>{new Date(config.updated_at).toLocaleDateString()}</Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEdit2 />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditConfig(config)}
                      aria-label="Edit"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => confirmDelete(config)}
                      aria-label="Delete"
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {configs.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>No configurations found</Text>
        )}
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingConfig ? 'Edit Configuration' : 'Add Configuration'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Key</FormLabel>
              <Input
                value={configForm.key}
                onChange={(e) => setConfigForm({ ...configForm, key: e.target.value })}
                placeholder="CONFIGURATION_KEY"
                isDisabled={!!editingConfig}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Value</FormLabel>
              <Textarea
                value={configForm.value}
                onChange={(e) => setConfigForm({ ...configForm, value: e.target.value })}
                placeholder="Configuration value"
                rows={4}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>Cancel</Button>
            <Button colorScheme="orange" onClick={handleSaveConfig}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete configuration "{configToDelete?.key}"?
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Configurations;
